import { useCallback, useMemo, useState } from "react";
import { ScrollView } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Button } from "@src/components/Button";
import { Icon } from "@src/components/Icon";
import { IconButton } from "@src/components/IconButton";
import { DialogModal } from "@src/components/DialogModal";
import { EmptyState } from "@src/components/screen-state";
import { ClipVideoPlayer, downloadVideo, handleDownloadError } from "@src/features/clips";
import type { Clip } from "@src/features/clips";
import { useClips, type ClipItem } from "@visioni/a3s-media/app";
import { type ConfirmedDumpItem } from "@visioni/a3s-dumping/app";
import { OBJECT_TYPE_LABELS, type ObjectType } from "@visioni/a3s-dumping/constants";
import { toISODateString } from "@visioni/a3s-codecs";
import { SiteId, AssetCameraKey } from "@visioni/a3s-schema";

/**
 * A confirmed dump plus a stable key. `(timestamp, detectedAt)` is unique per
 * camera (the DDB row's uniqueness), so it makes a collision-free React key
 * without the server sending a synthetic id.
 */
export interface DumpEntry {
  readonly key: string;
  readonly item: ConfirmedDumpItem;
}

export function toDumpEntry(item: ConfirmedDumpItem): DumpEntry {
  return { key: `${item.timestamp}#${item.detectedAt}`, item };
}

/** Human object label; tolerate an unknown key defensively (mirrors the report). */
function objectLabel(objectType: string): string {
  return objectType in OBJECT_TYPE_LABELS ? OBJECT_TYPE_LABELS[objectType as ObjectType] : objectType;
}

/**
 * Local-locale timestamp label, matching the rest of the Scene Compare page
 * (see `formatTimestamp` in SceneCompare.web.tsx). Support-operator tooling
 * formats in the viewer's local zone — no site timezone is plumbed here, which
 * is why the list endpoint returns raw ISO and the label is built client-side.
 */
function formatDetectedAt(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

/**
 * Adapt a media-service {@link ClipItem} to the clips feature's {@link Clip} so
 * the modal can reuse {@link ClipVideoPlayer}. Mirrors the adapter in
 * `DumpDetections.web.tsx` / `DumpingSearch.web.tsx`.
 */
function clipItemToClip(item: ClipItem): Clip {
  return {
    id: item.startTime,
    timestamp: item.startTime,
    thumbnailUrl: item.thumbnailUrl ?? null,
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- fallback for older deployed apps
    videoUrl: item.video?.url ?? item.videoUrl ?? "",
    duration: item.durationMs,
  };
}

/**
 * Resolve a confirmed dump's evidence clip and expose a download handler.
 * Adapts the WasteSense `useDumpClip` pattern to a {@link ConfirmedDumpItem}:
 * the clip identity is the dump's `timestamp` (its clip start), resolved via a
 * zero-width `useClips([t, t])` range query that returns the single matching
 * clip. Runs only while the modal is mounted (`enabled`) so the list itself
 * never fires a clip query per row.
 */
function useDumpClip(item: ConfirmedDumpItem, enabled: boolean) {
  const parsedSiteId = useMemo(() => SiteId.fromString(String(item.subject.site)), [item.subject.site]);
  const parsedCameraKey = useMemo(() => AssetCameraKey.fromString(String(item.subject.camera)), [item.subject.camera]);
  const normalizedTimestamp = useMemo(() => toISODateString(new Date(item.timestamp)), [item.timestamp]);

  const {
    data: clips,
    isPending,
    isError,
  } = useClips({
    siteId: parsedSiteId,
    assetCameraKey: parsedCameraKey,
    startTime: normalizedTimestamp,
    endTime: normalizedTimestamp,
    enabled,
  });

  const playerClip = useMemo(() => {
    if (!clips || clips.length === 0) return null;
    const exact = clips.find((c) => c.startTime === item.timestamp) ?? clips[0];
    return exact ? clipItemToClip(exact) : null;
  }, [clips, item.timestamp]);

  const [isDownloading, setIsDownloading] = useState(false);
  const handleDownload = useCallback(async () => {
    if (!playerClip?.videoUrl || isDownloading) return;
    try {
      setIsDownloading(true);
      await downloadVideo({ videoUrl: playerClip.videoUrl, timestamp: new Date(item.timestamp) });
    } catch (err: unknown) {
      handleDownloadError(err);
    } finally {
      setIsDownloading(false);
    }
  }, [playerClip?.videoUrl, isDownloading, item.timestamp]);

  return { playerClip, isPending, isError, isDownloading, handleDownload };
}

// Fixed media height for the modal clip. Large enough to review the frame,
// small enough to leave room for the metadata below on a typical viewport.
const MODAL_MEDIA_HEIGHT = 360;
const MODAL_MEDIA_ASPECT_RATIO = 16 / 9;
const DOWNLOAD_ICON_SIZE = 18;

/**
 * Full detail of a confirmed dump: the evidence clip plus Detected / Object /
 * Description / Reported by. Opened from a dump card in the sidebar list. The
 * clip query only runs while this modal is visible (`enabled`), so the list
 * doesn't fan out N clip requests.
 */
export function DumpDetailModal({
  entry,
  visible,
  onClose,
}: {
  entry: DumpEntry | null;
  visible: boolean;
  onClose: () => void;
}) {
  const { theme } = useUnistyles();
  // Guarded before the hook so hooks order stays stable: render nothing when
  // there's no selection. (The parent only mounts this with an entry.)
  if (!entry) return null;
  return <DumpDetailModalBody item={entry.item} visible={visible} onClose={onClose} theme={theme} />;
}

function DumpDetailModalBody({
  item,
  visible,
  onClose,
  theme,
}: {
  item: ConfirmedDumpItem;
  visible: boolean;
  onClose: () => void;
  theme: ReturnType<typeof useUnistyles>["theme"];
}) {
  const { playerClip, isPending, isError, isDownloading, handleDownload } = useDumpClip(item, visible);

  return (
    <DialogModal visible={visible} onClose={onClose} title="Confirmed dump" style={{ width: 640, maxWidth: "100%" }}>
      <View gap="3xl">
        {/* Evidence clip */}
        <View
          style={{
            height: MODAL_MEDIA_HEIGHT,
            borderRadius: 8,
            overflow: "hidden",
            backgroundColor: theme.colors.borderCard as string,
          }}
        >
          {playerClip ?
            <ClipVideoPlayer
              key={playerClip.id}
              clip={playerClip}
              autoPlay={false}
              fillParent
              screenWidth={MODAL_MEDIA_HEIGHT * MODAL_MEDIA_ASPECT_RATIO}
              screenHeight={MODAL_MEDIA_HEIGHT}
              selectedHour={0}
            />
          : <View flex={1} alignItems="center" justifyContent="center">
              <Text textStyle="xs/regular" color="textMuted">
                {isPending ?
                  "Loading clip…"
                : isError ?
                  "Clip unavailable"
                : "No clip found"}
              </Text>
            </View>
          }
        </View>

        {/* Meta row: Object, Detected, Reported by + download */}
        <View flexDirection="row" gap="md" alignItems="flex-start">
          <View flex={1} style={{ minWidth: 0 }}>
            <Meta label="Object" value={objectLabel(item.objectType)} />
          </View>
          <View flex={1} style={{ minWidth: 0 }}>
            <Meta label="Detected" value={formatDetectedAt(item.detectedAt)} />
          </View>
          <View flex={1} style={{ minWidth: 0 }}>
            <Meta label="Reported by" value={item.detectedBy} />
          </View>
          {playerClip ?
            <IconButton
              size="sm"
              title="Download clip"
              onPress={handleDownload}
              disabled={isDownloading}
              Icon={() => <Icon name="download" size={DOWNLOAD_ICON_SIZE} color={theme.colors.text} />}
            />
          : null}
        </View>

        {/* Description */}
        {item.description && item.description !== "" ?
          <View gap="xxs">
            <Text textStyle="xs/semibold" color="textMuted" letterSpacing={0.5} textTransform="uppercase">
              Description
            </Text>
            <Text textStyle="sm/regular" color="text">
              {item.description}
            </Text>
          </View>
        : null}
      </View>
    </DialogModal>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <View gap="xxs">
      <Text textStyle="xs/semibold" color="textMuted" letterSpacing={0.5} textTransform="uppercase">
        {label}
      </Text>
      <Text textStyle="sm/semibold" color="text">
        {value}
      </Text>
    </View>
  );
}

/** One compact row in the sidebar list: detected-at + object label. */
function DumpCard({ entry, onPress }: { entry: DumpEntry; onPress: () => void }) {
  const { theme } = useUnistyles();
  return (
    <div
      onClick={onPress}
      style={{
        padding: "8px 12px",
        borderRadius: 6,
        cursor: "pointer",
        marginBottom: 4,
        border: `1px solid ${theme.colors.borderCard as string}`,
        background: theme.colors.surface2?.background as string,
      }}
    >
      <Text textStyle="xs/semibold" color="text">
        {formatDetectedAt(entry.item.detectedAt)}
      </Text>
      <Text textStyle="xs/regular" color="textMuted">
        {objectLabel(entry.item.objectType)}
      </Text>
    </div>
  );
}

/**
 * The "Detections" sidebar section: a scrollable, newest-first list of the
 * camera's confirmed dumps with a "Load more" pager. Clicking a card opens the
 * detail modal. Presentation only — data + paging state come from the parent
 * (the Scene Compare screen owns the `useCameraConfirmedDumps` query so the
 * header count and the list stay in sync).
 */
export function DetectionsList({
  entries,
  isLoading,
  isError,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  entries: readonly DumpEntry[];
  isLoading: boolean;
  isError: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}) {
  const [selected, setSelected] = useState<DumpEntry | null>(null);

  return (
    <>
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingHorizontal: 12, paddingBottom: 12 }}>
        {isLoading ?
          <View py="3xl" alignItems="center">
            <Text textStyle="xs/regular" color="textMuted">
              Loading dumps…
            </Text>
          </View>
        : isError ?
          <View py="3xl" alignItems="center">
            <Text textStyle="xs/regular" color="textMuted">
              Couldn&apos;t load dumps
            </Text>
          </View>
        : entries.length === 0 ?
          <View py="3xl">
            <EmptyState
              icon="videoOutline"
              title="No confirmed dumps"
              message="No dumps have been reported here yet."
            />
          </View>
        : <>
            {entries.map((entry) => (
              <DumpCard key={entry.key} entry={entry} onPress={() => setSelected(entry)} />
            ))}
            {hasNextPage ?
              <View alignItems="center" mt="sm">
                <Button
                  label={isFetchingNextPage ? "Loading…" : "Load more"}
                  variant="link"
                  variantColor="grey"
                  onPress={onLoadMore}
                  disabled={isFetchingNextPage}
                />
              </View>
            : null}
          </>
        }
      </ScrollView>

      <DumpDetailModal entry={selected} visible={selected !== null} onClose={() => setSelected(null)} />
    </>
  );
}
